import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert, Paper, Stepper, Step, StepLabel,
  RadioGroup, FormControlLabel, Radio, Checkbox, FormGroup, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip
} from '@mui/material';
import { VolumeUp, VolumeOff } from '@mui/icons-material';
import api from '../../services/api';
import TextToSpeech from '../TextToSpeech';

// Helper to generate Likert options if not provided in definition
const getLikertOptions = (answerType) => {
  if (answerType === 'radio_likert_0_3') {
    return [
      { label: "Not at all", value: 0 },
      { label: "Several days", value: 1 },
      { label: "More than half the days", value: 2 },
      { label: "Nearly every day", value: 3 }
    ];
  }
  if (answerType === 'radio_likert_1_5') {
    return [
      { label: "Strongly Disagree", value: 1 },
      { label: "Disagree", value: 2 },
      { label: "Neutral", value: 3 },
      { label: "Agree", value: 4 },
      { label: "Strongly Agree", value: 5 }
    ];
  }
  return [];
};


const AssessmentRunner = ({
  token,
  testKey,
  conversationId,
  initiatedBy,
  onComplete,
  onCloseRequest,
  onSubmissionSuccess,
  onSubmitStart,
  onSubmitError,
  platformSettings,
  userSettings,
  therapistSettings,
  clientId,
  autoplayEnabled
}) => {
  const [testDefinition, setTestDefinition] = useState(null);
  const [answers, setAnswers] = useState({}); // { questionId: answerValue }
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [submissionResult, setSubmissionResult] = useState(null);
  const [showReview, setShowReview] = useState(false);

  // Adult-7 fix: notify the parent the moment submission succeeds, independent
  // of how the user later closes the dialog. Without this, if the user
  // dismisses the outer modal via the X button or the Escape key after
  // submitting (instead of clicking the inner "Close" button on the
  // "Assessment Complete" sub-dialog), the parent treats it as a cancel and
  // emits "Assessment cancelled." even though the submission saved correctly.
  const onSubmissionSuccessRef = useRef(onSubmissionSuccess);
  useEffect(() => { onSubmissionSuccessRef.current = onSubmissionSuccess; }, [onSubmissionSuccess]);
  const successFiredRef = useRef(false);
  useEffect(() => {
    if (submissionResult && !successFiredRef.current) {
      successFiredRef.current = true;
      try {
        onSubmissionSuccessRef.current?.(submissionResult);
      } catch (err) {
        console.error('[AssessmentRunner] onSubmissionSuccess handler threw:', err);
      }
    }
  }, [submissionResult]);

  // TTS-related state
  const [streamingSupported, setStreamingSupported] = useState(null);
  const [providerDetected, setProviderDetected] = useState(false);
  const [assessmentTTSMuted, setAssessmentTTSMuted] = useState(false);
  const [currentlyPlayingQuestionId, setCurrentlyPlayingQuestionId] = useState(null);
  const playingAudioRef = useRef(null);

  const presentationMode = testDefinition?.defaultPresentationMode || 'single_page';
  const allowReview = testDefinition?.allowReviewBeforeSubmit !== false; // Default true

  useEffect(() => {
    const fetchTest = async () => {
      setIsLoading(true);
      setError('');
      // Bounded retry with backoff. The definition GET was previously a single
      // attempt with no recovery, so one transient server error / DB timeout
      // dropped the assessment (observed on cssrs-screener mid-safety-pathway).
      // Only retry TRANSIENT failures (network error / 5xx); a 4xx (e.g. 404
      // missing definition) will not improve on retry. Mirrors the submit path.
      const MAX_ATTEMPTS = 3;
      let lastErr;
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const response = await api.getTestDefinitionDetails(testKey, token);
          setTestDefinition(response.data);
          // Initialize answers state
          const initialAnswers = {};
          response.data.questions.forEach(q => {
            initialAnswers[q.questionId] = q.answerType === 'checkbox_custom' ? [] : '';
          });
          setAnswers(initialAnswers);
          setIsLoading(false);
          return;
        } catch (err) {
          lastErr = err;
          const status = err.response?.status;
          const isTransient = !err.response || status >= 500;
          if (!isTransient || attempt === MAX_ATTEMPTS) break;
          console.warn(`[AssessmentRunner] load attempt ${attempt}/${MAX_ATTEMPTS} for "${testKey}" failed (${status || 'no response'}) — retrying`);
          await new Promise(resolve => setTimeout(resolve, 400 * attempt)); // 400ms, 800ms
        }
      }
      console.error("Error fetching test definition:", lastErr);
      setError(lastErr?.response?.data?.msg || `Failed to load assessment: ${testKey}`);
      setIsLoading(false);
    };
    if (testKey) {
      fetchTest();
    }
  }, [testKey, token]);

  // Provider detection for TTS
  useEffect(() => {
    const detectProvider = async () => {
      if (!token) return;
      try {
        const response = await api.get('/tts/provider-info', token, {
          params: { interface: 'assessment' }
        });
        const provider = response?.data?.provider?.toLowerCase() || 'elevenlabs';
        setStreamingSupported(provider === 'elevenlabs');
      } catch (error) {
        console.error('[AssessmentRunner] Failed to detect TTS provider:', error);
        setStreamingSupported(false);
      } finally {
        setProviderDetected(true);
      }
    };
    detectProvider();
  }, [token]);

  // Autoplay effect for one_at_a_time mode
  useEffect(() => {
    if (
      autoplayEnabled &&
      !assessmentTTSMuted &&
      presentationMode === 'one_at_a_time' &&
      testDefinition &&
      !isLoading &&
      !showReview &&
      !submissionResult &&
      providerDetected
    ) {
      const currentQuestion = testDefinition.questions[currentQuestionIndex];
      if (currentQuestion) {
        setCurrentlyPlayingQuestionId(currentQuestion.questionId);
      }
    }
  }, [
    currentQuestionIndex,
    autoplayEnabled,
    assessmentTTSMuted,
    presentationMode,
    testDefinition,
    isLoading,
    showReview,
    submissionResult,
    providerDetected
  ]);

  const handleAnswerChange = (questionId, value, answerType) => {
    setAnswers(prev => {
      if (answerType === 'checkbox_custom') {
        const currentSelection = prev[questionId] || [];
        const newSelection = currentSelection.includes(value)
          ? currentSelection.filter(item => item !== value)
          : [...currentSelection, value];
        return { ...prev, [questionId]: newSelection };
      }
      return { ...prev, [questionId]: value };
    });
  };

  const handleNext = () => {
    if (currentQuestionIndex < testDefinition.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // Last question, proceed to review or submit
      if (allowReview) {
        setShowReview(true);
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };
  
  const allRequiredAnswered = useCallback(() => {
    if (!testDefinition) return false;
    return testDefinition.questions.every(q => {
        if (!q.required) return true;
        const answer = answers[q.questionId];
        if (q.answerType === 'checkbox_custom') return Array.isArray(answer) && answer.length > 0;
        return answer !== '' && answer !== undefined && answer !== null;
    });
  }, [testDefinition, answers]);


  const handleSubmit = async () => {
    if (!testDefinition || !testDefinition.questions?.length) {
      setError("Assessment not loaded. Please close and try again.");
      return;
    }
    // Guard: ensure at least one answer was provided (prevents auto-submit race)
    const answeredCount = Object.values(answers).filter(v =>
      v !== '' && v !== undefined && v !== null && !(Array.isArray(v) && v.length === 0)
    ).length;
    if (answeredCount === 0) {
      console.warn('[AssessmentRunner] Submission blocked — no answers provided');
      setError("Please answer the questions before submitting.");
      return;
    }
    if (!allRequiredAnswered()) {
        setError("Please answer all required questions before submitting.");
        setShowReview(false); // Go back to questions if review was shown
        return;
    }
    setIsLoading(true);
    setError('');
    // Signal that a submission is in-flight so a close during the (possibly
    // multi-second, retrying) submit window is NOT mistaken for a cancellation
    // (Tom ticket: spurious "Assessment cancelled" after a successful submit).
    try { onSubmitStart?.(); } catch (_) { /* best-effort */ }

    const formattedAnswers = Object.entries(answers).map(([questionId, answerValue]) => ({
      questionId,
      answerValue
    }));
    const submissionData = {
      testKey,
      answers: formattedAnswers,
      conversationId,
      initiatedBy
    };

    // Submit with retry + localStorage backup. Earlier production tests showed
    // submissions that never reached the server, leaving the user thinking they
    // had completed the assessment while no result was saved. Retry a few times
    // before falling back to local persistence so the user can recover.
    const BACKUP_KEY = `assessment-backup:${conversationId}:${testKey}`;
    const maxAttempts = 3;
    let lastErr = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await api.submitAssessment(submissionData, token);
        // Success — clear any prior backup and exit retry loop
        try { localStorage.removeItem(BACKUP_KEY); } catch (_) { /* localStorage may be disabled */ }
        setSubmissionResult(response.data); // { msg, score, interpretation, submittedResult }
        setIsLoading(false);
        return;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        console.warn(`[AssessmentRunner] submit attempt ${attempt}/${maxAttempts} failed (status=${status}):`, err?.message || err);
        // Don't retry on 4xx that are unrecoverable (auth errors, validation): give up immediately
        if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
          break;
        }
        // Exponential backoff before next attempt: 500ms, 1500ms
        if (attempt < maxAttempts) {
          await new Promise(r => setTimeout(r, 500 * Math.pow(3, attempt - 1)));
        }
      }
    }

    // All attempts failed — persist answers to localStorage and surface a recoverable error
    try {
      localStorage.setItem(BACKUP_KEY, JSON.stringify({
        testKey, conversationId, answers: formattedAnswers, initiatedBy,
        savedAt: new Date().toISOString()
      }));
      console.error('[AssessmentRunner] All submit attempts failed — answers persisted to localStorage with key:', BACKUP_KEY);
      setError(
        (lastErr?.response?.data?.msg || 'Failed to submit assessment after multiple attempts.') +
        ' Your responses are saved locally — please try again, or close and re-open the assessment to retry.'
      );
    } catch (storageErr) {
      console.error('[AssessmentRunner] All submit attempts failed AND localStorage backup failed:', storageErr);
      setError(lastErr?.response?.data?.msg || 'Failed to submit assessment. Please try again or contact support.');
    }
    // Submission is no longer in-flight (it failed). Clear the in-flight signal
    // so a subsequent close is treated as a genuine cancel again.
    try { onSubmitError?.(); } catch (_) { /* best-effort */ }
    setIsLoading(false);
  };

  const setPlayingAudio = (audioInstance) => {
    playingAudioRef.current = audioInstance;
  };

  const handlePlaybackComplete = () => {
    setCurrentlyPlayingQuestionId(null);
  };

  const renderQuestion = (question, qIndex) => {
    const answerValue = answers[question.questionId];
    let options = question.answerOptions || [];
    if (options.length === 0 && (question.answerType === 'radio_likert_0_3' || question.answerType === 'radio_likert_1_5')) {
        options = getLikertOptions(question.answerType);
    }

    const shouldAutoplayThisQuestion =
      autoplayEnabled &&
      !assessmentTTSMuted &&
      presentationMode === 'one_at_a_time' &&
      question.questionId === currentlyPlayingQuestionId;

    return (
      <Paper key={question.questionId} sx={{ p: 2, mb: presentationMode === 'single_page' ? 2 : 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ flex: 1 }}>
            {`Question ${qIndex + 1}: ${question.text}`} {question.required && '*'}
          </Typography>
          {platformSettings && userSettings && (
            <TextToSpeech
              text={question.text}
              size="small"
              senderRole="ai_therapist"
              platformSettings={platformSettings}
              userSettings={userSettings}
              therapistSettings={therapistSettings}
              clientId={clientId}
              token={token}
              useBackendTTS={true}
              ttsInterface="assessment"
              useStreaming={streamingSupported}
              autoplay={shouldAutoplayThisQuestion}
              onPlaybackComplete={handlePlaybackComplete}
              setPlayingAudio={setPlayingAudio}
              disabled={isLoading || !!submissionResult || !providerDetected}
            />
          )}
        </Box>
        {question.instructions && <Typography variant="caption" display="block" gutterBottom sx={{mb:1}}>{question.instructions}</Typography>}

        {question.answerType.startsWith('radio_') && (
          <RadioGroup
            value={answerValue || ''}
            onChange={(e) => handleAnswerChange(question.questionId, e.target.value, question.answerType)}
          >
            {options.map((opt) => (
              <FormControlLabel key={opt.value.toString()} value={opt.value.toString()} control={<Radio />} label={`${opt.label}${opt.subText ? ` (${opt.subText})` : ''}`} />
            ))}
          </RadioGroup>
        )}

        {question.answerType === 'checkbox_custom' && (
          <FormGroup>
            {options.map((opt) => (
              <FormControlLabel
                key={opt.value.toString()}
                control={
                  <Checkbox
                    checked={(answerValue || []).includes(opt.value.toString())}
                    onChange={(e) => handleAnswerChange(question.questionId, opt.value.toString(), question.answerType)}
                  />
                }
                label={`${opt.label}${opt.subText ? ` (${opt.subText})` : ''}`}
              />
            ))}
          </FormGroup>
        )}
        
        {question.answerType === 'number_input' && (
            <TextField 
                type="number"
                value={answerValue || ''}
                onChange={(e) => handleAnswerChange(question.questionId, e.target.value, question.answerType)}
                variant="outlined"
                size="small"
            />
        )}
        {(question.answerType === 'text_input_short' || question.answerType === 'text_input_long') && (
            <TextField 
                fullWidth
                multiline={question.answerType === 'text_input_long'}
                rows={question.answerType === 'text_input_long' ? 3 : 1}
                value={answerValue || ''}
                onChange={(e) => handleAnswerChange(question.questionId, e.target.value, question.answerType)}
                variant="outlined"
            />
        )}
      </Paper>
    );
  };
  
  const renderReviewPage = () => {
    return (
        <Box>
            <Typography variant="h6" gutterBottom>Review Your Answers</Typography>
            {testDefinition.questions.map((q, i) => {
                const answer = answers[q.questionId];
                let displayAnswer = 'Not answered';
                if (q.answerType === 'checkbox_custom') {
                    displayAnswer = Array.isArray(answer) && answer.length > 0 ? answer.join(', ') : 'Not answered';
                } else if (answer !== '' && answer !== undefined && answer !== null) {
                    const option = (q.answerOptions || getLikertOptions(q.answerType)).find(opt => opt.value.toString() === answer.toString());
                    displayAnswer = option ? option.label : answer.toString();
                }
                return (
                    <Paper key={`review-${q.questionId}`} sx={{p:1, mb:1}}>
                        <Typography variant="subtitle2">{`Q${i+1}: ${q.text}`}</Typography>
                        <Typography variant="body2" color="text.secondary">Your answer: {displayAnswer}</Typography>
                    </Paper>
                );
            })}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button variant="outlined" onClick={() => {setShowReview(false); setCurrentQuestionIndex(testDefinition.questions.length -1);}}>Edit Answers</Button>
                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isLoading}>
                    {isLoading ? <CircularProgress size={24} /> : "Submit Assessment"}
                </Button>
            </Box>
        </Box>
    );
  };


  if (isLoading && !testDefinition) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>;
  }

  // Only a LOAD failure replaces the runner: with no definition there are no
  // questions to show, so the bare alert is all there is. A SUBMIT failure must
  // not, because this return also removed the "Cancel Assessment" button at the
  // foot of the form. In a call the runner sits in a dialog that deliberately
  // ignores backdrop clicks and Escape, so its Cancel was the only way out: a
  // client whose submit failed was trapped and the therapist had to end the
  // call to free them (ST-20260916-0006). A submit error now renders inline
  // above the questions, where Cancel is still reachable.
  if (error && !submissionResult && !testDefinition) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!testDefinition) {
    return <Typography>Assessment not found or failed to load.</Typography>;
  }

  if (submissionResult) {
    return (
      <Dialog open={true} onClose={() => onComplete(submissionResult)} maxWidth="sm" fullWidth>
        <DialogTitle>Assessment Complete</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            {submissionResult.msg || 'Your assessment has been submitted successfully.'}
          </Typography>
          <Typography variant="body1">
            Your therapist will review these results and discuss them with you.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onComplete(submissionResult)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    );
  }
  
  if (showReview) {
    return renderReviewPage();
  }

  return (
    <Box sx={{p:2}}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5">{testDefinition.fullName}</Typography>
          <Typography variant="body2" color="text.secondary">{testDefinition.description}</Typography>
        </Box>
        {autoplayEnabled && (
          <Tooltip title={assessmentTTSMuted ? "Unmute question audio" : "Mute question audio"}>
            <IconButton
              onClick={() => setAssessmentTTSMuted(!assessmentTTSMuted)}
              size="medium"
              color={assessmentTTSMuted ? "default" : "primary"}
            >
              {assessmentTTSMuted ? <VolumeOff /> : <VolumeUp />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {presentationMode === 'one_at_a_time' && (
        <Stepper activeStep={currentQuestionIndex} alternativeLabel sx={{mb:3}}>
          {testDefinition.questions.map((q, index) => (
            <Step key={q.questionId}>
              <StepLabel></StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {error && <Alert severity="error" sx={{mb:2}}>{error}</Alert>}

      {presentationMode === 'single_page' ? (
        testDefinition.questions.map((q, i) => renderQuestion(q, i))
      ) : (
        renderQuestion(testDefinition.questions[currentQuestionIndex], currentQuestionIndex)
      )}

      <Box sx={{ display: 'flex', justifyContent: presentationMode === 'one_at_a_time' ? 'space-between' : 'flex-end', mt: 3 }}>
        {presentationMode === 'one_at_a_time' && (
          <Button variant="outlined" onClick={handlePrevious} disabled={currentQuestionIndex === 0 || isLoading}>
            Previous
          </Button>
        )}
        
        {presentationMode === 'one_at_a_time' ? (
          <Button variant="contained" onClick={handleNext} disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : (currentQuestionIndex === testDefinition.questions.length - 1 ? (allowReview ? "Review Answers" : "Submit") : "Next")}
          </Button>
        ) : (
          <Button variant="contained" color="primary" onClick={allowReview ? () => setShowReview(true) : handleSubmit} disabled={isLoading || !allRequiredAnswered()}>
            {isLoading ? <CircularProgress size={24} /> : "Submit Assessment"}
          </Button>
        )}
      </Box>
       <Button onClick={onCloseRequest} color="inherit" sx={{mt:2}}>Cancel Assessment</Button>
    </Box>
  );
};

export default AssessmentRunner;
